import React, { memo, useContext } from 'react';
import { Panel } from '@xyflow/react';
import { CapChartMap } from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/CapChart/CapChartMap';
import { ReactFlowControls } from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/CapChart/ReactFlowControls.tsx';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { CapChartEmptyState } from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/CapChart/CapChartEmptyState.tsx';
import { useTranslationCapChart } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { StyledControlButton } from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/CapChart/StyledControlButton.tsx';
import { CapChartPageContext } from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/CapChartPage.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { CapChartSecuritiesDrawer } from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/CapChart/CapChartSecuritiesDrawer/CapChartSecuritiesDrawer.tsx';
import styled from '@emotion/styled';

interface Props {
	hasElements: boolean;
	onSave: () => void;
	onReset: () => void;
}

export const CapChartContent = memo(({ hasElements, onSave, onReset }: Props) => {
	const tCapChart = useTranslationCapChart();
	const capChartContext = useContext(CapChartPageContext);
	const { downloadLoading } = capChartContext.capChartData;

	const {
		inSecuritiesMode,
		showSecuritiesDrawer,
		handleResetSecuritiesView,
		onToggleSpecifySecuritiesDrawer,
		selectedSecurities,
		setSelectSecurities,
		chartSecurities,
		fetchWithSelectedSecuritiesView,
	} = capChartContext.capChartSecurities;

	return hasElements ? (
		<>
			<Panel position="top-left">
				<CapChartMap />
			</Panel>
			<ReactFlowControls
				onSave={onSave}
				onReset={onReset}
				position="top-right"
				children={
					inSecuritiesMode ? (
						<StyledEditSecuritiesButton
							onClick={onToggleSpecifySecuritiesDrawer}
							title={tCapChart('specify_securities.edit_tooltip_title')}
							withbackground={true}
						>
							<Icon variant="pencil-edit-line" size="xl" />
						</StyledEditSecuritiesButton>
					) : undefined
				}
			/>
			{downloadLoading && <InlineLoader />}
			<CapChartSecuritiesDrawer
				opened={showSecuritiesDrawer}
				onClose={onToggleSpecifySecuritiesDrawer}
				securities={chartSecurities || []}
				selectedSecurities={selectedSecurities}
				setSelectSecurities={setSelectSecurities}
				applySecuritiesView={fetchWithSelectedSecuritiesView}
				resetSecuritiesView={handleResetSecuritiesView}
			/>
		</>
	) : (
		<CapChartEmptyState />
	);
});

const StyledEditSecuritiesButton = styled(StyledControlButton)`
	border-width: 1px !important;
	border-style: solid !important;
	border-radius: 0.5rem !important;
	width: 100% !important;
	margin-top: 1rem;
`;
